import { currentAuthEpoch } from '@/utils/authRecovery'
import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { useCentrifugeStore } from '@/modules/centrifuge/stores/centrifugeStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import axios from 'axios'
import { defineStore } from 'pinia'
import type { IImportCellCorrection } from '../types/IImportCellCorrection'
import type { IImportHistory, IImportHistoryResponse } from '../types/IImportHistory'
import type { IImportColumn } from '../types/IImportColumn'
import type { IImportRowsResponse } from '../types/IImportRowsResponse'
import type { TImportRowsStatus } from '../types/TImportRowFilter'
import type { IImportPreviewRow } from '../types/IImportPreviewRow'
import type { IImportExtraParam, IImportSchema } from '../types/IImportSchema'
import type { IImportType } from '../types/IImportType'
import type { IImportUploadResponse } from '../types/IImportUploadResponse'
import type { IImportValidateResponse } from '../types/IImportValidateResponse'
import type { IImportActiveItem, IImportCompletedEvent, IImportProgressEvent } from '../types/IImportActive'
import type { IImportUnfinishedResponse } from '../types/IImportUnfinished'

const IMPORTS_URL = `${BASE_URL}/api/exchange/imports`

const inFlightUnfinished = new Map<string, Promise<IImportUnfinishedResponse | null>>()
const inFlightActive = new Map<string, Promise<IImportActiveItem[]>>()

function isMatchingImportType(taskType?: string | null, activeType?: string | null): boolean {
  if (!taskType || !activeType) return true
  const a = taskType.trim().toLowerCase()
  const b = activeType.trim().toLowerCase()
  if (a === b) return true
  if (a.replace(/s$/, '') === b.replace(/s$/, '')) return true
  return false
}

function importErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data && typeof data === 'object') {
      if (typeof data.error === 'string' && data.error)
        return data.error
      if (typeof data.detail === 'string' && data.detail)
        return data.detail
    }
  }
  return getApiError(error, fallback)
}

/**
 * Универсальный импорт `/api/exchange/imports/{type}/`.
 * Страница мастера только вызывает экшены и рисует ответ.
 */
export const useImportStore = defineStore('importStore', {
  state: () => ({
    importTypes: [] as IImportType[],
    columns: [] as IImportColumn[],
    extraParams: [] as IImportExtraParam[],
    acceptedExtensions: [] as string[],
    upload: null as IImportUploadResponse | null,
    validation: null as IImportValidateResponse | null,
    rows: [] as IImportPreviewRow[],
    rowsPage: 1,
    rowsTotalPages: 1,
    rowsCount: 0,
    rowsStructure: 'flat' as 'flat' | 'tree',
    rowsPaginated: true,
    rowsNotice: null as string | null,
    history: [] as IImportHistory[],
    historyNext: null as string | null,
    historyPrevious: null as string | null,
    historyCount: 0,
    historyDetail: null as IImportHistory | null,
    activeTasks: [] as IImportActiveItem[],
    activeImport: null as IImportActiveItem | null,
    activeImportType: null as string | null,
    activeImportChecked: false,
    unfinishedCount: 0,
    unfinishedFiles: [] as string[],
  }),


  actions: {
    clearSession() {
      this.upload = null
      this.validation = null
      this.rows = []
      this.rowsPage = 1
      this.rowsTotalPages = 1
      this.rowsCount = 0
      this.rowsStructure = 'flat'
      this.rowsPaginated = true
      this.rowsNotice = null
    },

    /** Справочник типов: key модуля подставляется в /import/{key}. */
    async fetchImportTypes(): Promise<IImportType[]> {
      const feedbackStore = useFeedbackStore()
      try {
        const response = await axios.get<IImportType[]>(`${IMPORTS_URL}/types/`)
        this.importTypes = Array.isArray(response.data) ? response.data : []
        return this.importTypes
      } catch (error) {
        this.importTypes = []
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить типы импорта'),
        })
        return []
      }
    },

    /** Колонки примера и допустимые расширения схемы. */
    async fetchColumns(importType: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const [columnsResponse, schemaResponse] = await Promise.all([
          axios.get<IImportColumn[]>(`${IMPORTS_URL}/${importType}/columns/`),
          axios.get<IImportSchema>(`${IMPORTS_URL}/${importType}/schema/`),
        ])
        this.columns = Array.isArray(columnsResponse.data) ? columnsResponse.data : []
        this.extraParams = Array.isArray(schemaResponse.data?.extra_params)
          ? schemaResponse.data.extra_params
          : []
        this.acceptedExtensions = Array.isArray(schemaResponse.data?.accepted_extensions)
          ? schemaResponse.data.accepted_extensions
          : []
        return true
      } catch (error) {
        this.columns = []
        this.extraParams = []
        this.acceptedExtensions = []
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить схему импорта'),
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async uploadImportFile(importType: string, file: File): Promise<IImportUploadResponse | null> {
      const feedbackStore = useFeedbackStore()
      const filesStore = useFilesStore()
      filesStore.startUpload('create', [{ name: file.name, size: file.size, type: file.type }])
      try {
        const form = new FormData()
        form.append('file', file)
        const response = await axios.post<IImportUploadResponse>(
          `${IMPORTS_URL}/${importType}/upload/`,
          form,
          {
            onUploadProgress: event => {
              filesStore.onUploadProgress(event.loaded, event.total ?? null)
            },
          },
        )
        filesStore.finishUpload(true)
        this.upload = response.data
        this.validation = null
        return response.data
      } catch (error) {
        filesStore.finishUpload(false)
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить файл'),
        })
        return null
      } finally {
        setTimeout(() => filesStore.resetUpload(), 400)
      }
    },

    async validateImport(
      importType: string,
      uuid: string,
      columnMapping: Record<string, string>,
      corrections: IImportCellCorrection[] = [],
      extraParams: Record<string, string> = {},
    ): Promise<IImportValidateResponse | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IImportValidateResponse>(
          `${IMPORTS_URL}/${importType}/validate/`,
          {
            uuid,
            column_mapping: columnMapping,
            corrections: corrections.map(item => ({
              row: Number(item.row),
              column: String(item.column),
              value: item.value == null ? '' : String(item.value),
            })),
            extra_params: extraParams,
          },
        )
        this.validation = response.data
        return response.data
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось проверить файл'),
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Страница строк файла.
     * status: all — все строки, error — с ошибками, warning — с предупреждениями,
     * skipped — пропускаемые (запись уже в системе, данные совпадают).
     */
    async fetchImportRows(
      fileUuid: string,
      status: TImportRowsStatus,
      page = 1,
      pageSize = 25,
      importType?: string,
    ): Promise<IImportRowsResponse | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const type = importType || this.activeImportType
        if (type?.startsWith('ep_') && this.validation?.uuid === fileUuid) {
          const validation = this.validation
          const allRows = validation.preview_rows.filter(row => {
            if (status === 'error')
              return !row.is_valid
            if (status === 'warning')
              return row.warnings.length > 0
            if (status === 'skipped')
              return Boolean(row.is_skipped)
            return true
          })
          const results = allRows.slice((page - 1) * pageSize, page * pageSize)
          this.rows = results
          this.rowsPage = page
          this.rowsTotalPages = Math.max(1, Math.ceil(allRows.length / pageSize))
          this.rowsCount = allRows.length
          this.rowsStructure = 'flat'
          this.rowsPaginated = true
          this.rowsNotice = null
          return {
            uuid: fileUuid, import_type: type, file_name: validation.file_name ?? null,
            status, structure: 'flat', paginated: true,
            total_rows: validation.total_rows, valid_rows_count: validation.valid_rows_count,
            error_rows_count: validation.error_rows_count, warning_rows_count: validation.warning_rows_count,
            skipped_rows_count: validation.skipped_rows_count, skipped: validation.skipped,
            page, page_size: pageSize, total_pages: this.rowsTotalPages, count: allRows.length, results,
          }
        }
        const url = type ? `${IMPORTS_URL}/${type}/${fileUuid}/rows/` : `${IMPORTS_URL}/${fileUuid}/rows/`
        const response = await axios.get<IImportRowsResponse>(url, {
          params: {
            status,
            page,
            page_size: pageSize,
          },
        })
        const data = response.data
        this.rows = Array.isArray(data?.results) ? data.results : []
        this.rowsPage = data?.page ?? page
        this.rowsTotalPages = data?.total_pages ?? 1
        this.rowsCount = data?.count ?? this.rows.length
        this.rowsStructure = data?.structure === 'tree' ? 'tree' : 'flat'
        this.rowsPaginated = data?.paginated !== false
        this.rowsNotice = typeof data?.error === 'string' && data.error ? data.error : null
        return data
      } catch (error) {
        this.rows = []
        this.rowsPage = 1
        this.rowsTotalPages = 1
        this.rowsCount = 0
        this.rowsStructure = 'flat'
        this.rowsPaginated = true
        this.rowsNotice = null
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить строки импорта'),
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * История импортов. Пагинация курсорная: next/previous, page_size.
     * import_type и status — фильтры списка.
     */
    async fetchImportHistory(options?: {
      importType?: string
      status?: string
      cursor?: string
      pageSize?: number
    }): Promise<IImportHistoryResponse | null> {
      const feedbackStore = useFeedbackStore()
      this.history = []
      this.historyNext = null
      this.historyPrevious = null
      this.historyCount = 0
      try {
        const params: Record<string, string | number> = {}
        if (options?.importType)
          params.import_type = options.importType
        if (options?.status)
          params.status = options.status
        if (options?.cursor)
          params.cursor = options.cursor
        if (options?.pageSize)
          params.page_size = options.pageSize

        const type = options?.importType || this.activeImportType
        if (!type) {
          throw new Error('Не указан тип импорта для получения истории')
        }

        const response = await axios.get<IImportHistoryResponse>(`${IMPORTS_URL}/${type}/history/`, { params })
        const data = response.data
        this.history = Array.isArray(data?.results) ? data.results : []
        this.historyNext = data?.next ?? null
        this.historyPrevious = data?.previous ?? null
        this.historyCount = data?.count ?? this.history.length
        return data
      } catch (error) {
        this.history = []
        this.historyNext = null
        this.historyPrevious = null
        this.historyCount = 0
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить историю импорта'),
        })
        return null
      }
    },

    /** Одна запись GET /api/exchange/imports/{type}/history/{id}/. */
    async fetchImportHistoryById(id: number, importType?: string): Promise<IImportHistory | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const type = importType || this.activeImportType
        if (!type) {
          throw new Error('Не указан тип импорта')
        }
        const response = await axios.get<IImportHistory>(`${IMPORTS_URL}/${type}/history/${id}/`)
        this.historyDetail = response.data
        return response.data
      } catch (error) {
        this.historyDetail = null
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось загрузить запись истории импорта'),
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Скачивание исходного файла импорта GET /api/exchange/imports/{type}/history/{id}/download/. */
    async downloadImportHistoryFile(id: number, importType?: string, fileName?: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const type = importType || this.activeImportType
        if (!type) {
          throw new Error('Не указан тип импорта')
        }
        const response = await axios.get(`${IMPORTS_URL}/${type}/history/${id}/download/`, {
          responseType: 'blob',
        })
        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName || `import_${id}`
        link.click()
        URL.revokeObjectURL(link.href)
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось скачать файл импорта'),
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** GET /api/exchange/imports/{type}/unfinished/ с дедупликацией параллельных запросов */
    async fetchUnfinishedImports(importType: string, throwOnError = false): Promise<IImportUnfinishedResponse | null> {
      const epoch = currentAuthEpoch()
      const key = `${epoch}:${importType}:${throwOnError}`
      const existing = inFlightUnfinished.get(key)
      if (existing)
        return existing

      const promise = (async () => {
        try {
          const response = await axios.get<IImportUnfinishedResponse>(`${IMPORTS_URL}/${importType}/unfinished/`)
          if (epoch !== currentAuthEpoch()) return null
          const data = response.data
          this.unfinishedCount = Number(data?.count || 0)
          this.unfinishedFiles = Array.isArray(data?.files) ? data.files : []
          return data
        } catch (error) {
          if (epoch !== currentAuthEpoch()) return null
          if (throwOnError) throw error
          this.unfinishedCount = 0
          this.unfinishedFiles = []
          return null
        } finally {
          inFlightUnfinished.delete(key)
        }
      })()

      inFlightUnfinished.set(key, promise)
      return promise
    },

    /** GET /api/exchange/imports/{type}/active/ с дедупликацией параллельных запросов */
    async fetchActiveImport(importType: string, throwOnError = false): Promise<IImportActiveItem[]> {
      const epoch = currentAuthEpoch()
      const key = `${epoch}:${importType}:${throwOnError}`
      const existing = inFlightActive.get(key)
      if (existing)
        return existing

      const feedbackStore = useFeedbackStore()
      const promise = (async () => {
        try {
          const response = await axios.get<IImportActiveItem[]>(`${IMPORTS_URL}/${importType}/active/`)
          if (epoch !== currentAuthEpoch()) return []
          const tasks = Array.isArray(response.data) ? response.data : []
          this.activeTasks = tasks
          this.activeImport = tasks.find(t => t.status === 'in_progress') || tasks[0] || null
          return tasks
        } catch (error) {
          if (epoch !== currentAuthEpoch()) return []
          if (throwOnError) throw error
          this.activeTasks = []
          this.activeImport = null
          feedbackStore.showToast({
            type: 'error',
            message: importErrorMessage(error, 'Не удалось проверить активный импорт'),
          })
          return []
        } finally {
          inFlightActive.delete(key)
        }
      })()

      inFlightActive.set(key, promise)
      return promise
    },

    /** Инициализация отслеживания активных импортов и незавершенных файлов */
    async watchActiveImport(importType: string) {
      this.activeImportType = importType
      this.activeImportChecked = false
      await Promise.all([
        this.fetchActiveImport(importType),
        this.fetchUnfinishedImports(importType),
      ])
      this.activeImportChecked = true
    },

    /** Обновление прогресса выполнения по Centrifugo import_progress (из личного канала user:user#sub) */
    applyProgressRealtime(payload: any) {
      const rawData = payload?.data ?? payload
      if (!rawData) return

      // Бэкенд шлёт массив активных задач [ ... ]
      if (Array.isArray(rawData)) {
        const tasks = rawData as IImportActiveItem[]
        const currentType = this.activeImportType?.toLowerCase()
        const matchingTasks = currentType
          ? tasks.filter(t => isMatchingImportType(t.import_type, currentType))
          : tasks

        const activeList = matchingTasks.filter(t => t.status === 'in_progress' || t.status === 'pending')
        this.activeTasks = activeList
        this.activeImport = activeList.find(t => t.status === 'in_progress') || activeList[0] || null

        // Синхронизируем с историей (если открыта модалка истории)
        if (this.history.length > 0) {
          for (const task of tasks) {
            const histIndex = this.history.findIndex(h => Number(h.id) === Number(task.id))
            if (histIndex !== -1) {
              const hist = this.history[histIndex]
              this.history[histIndex] = {
                ...hist,
                percent: task.percent ?? hist.percent,
                imported_items: task.imported_items ?? hist.imported_items,
                failed_items: task.failed_items ?? hist.failed_items,
                total_items: task.total_items ?? hist.total_items,
                status: task.status || hist.status,
                status_display: task.status_display || hist.status_display,
              }
            }
          }
        }
        return
      }

      // Единичный объект (обратная совместимость)
      const data = rawData
      const id = Number(data.import_history_id || data.id)
      if (!id) return

      const taskIndex = this.activeTasks.findIndex(t => Number(t.id) === id)
      if (taskIndex !== -1) {
        const current = this.activeTasks[taskIndex]
        this.activeTasks[taskIndex] = {
          ...current,
          percent: data.percent ?? current.percent,
          imported_items: data.imported_items ?? current.imported_items,
          failed_items: data.failed_items ?? current.failed_items,
          total_items: data.total_items ?? current.total_items,
          status: data.status || current.status,
          status_display: data.status_display || current.status_display,
        }
        this.activeTasks = [...this.activeTasks]
        if (this.activeImport && Number(this.activeImport.id) === id) {
          this.activeImport = this.activeTasks[taskIndex]
        }
      }

      const histIndex = this.history.findIndex(h => Number(h.id) === id)
      if (histIndex !== -1) {
        const hist = this.history[histIndex]
        this.history[histIndex] = {
          ...hist,
          percent: data.percent ?? hist.percent,
          imported_items: data.imported_items ?? hist.imported_items,
          failed_items: data.failed_items ?? hist.failed_items,
          total_items: data.total_items ?? hist.total_items,
          status: data.status || hist.status,
          status_display: data.status_display || hist.status_display,
        }
      }
    },

    /** POST /api/exchange/imports/{type}/cancel/ с id (отмена задачи в очереди или процессе) */
    async cancelImportById(importType: string, id: number): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(`${IMPORTS_URL}/${importType}/cancel/`, { id })
        // Актуализируем статус локально
        const task = this.activeTasks.find(t => Number(t.id) === id)
        if (task) {
          task.status = 'cancelled'
          task.status_display = 'Отменено'
        }
        const hist = this.history.find(h => Number(h.id) === id)
        if (hist) {
          hist.status = 'cancelled'
          hist.status_display = 'Отменено'
          hist.can_cancel = false
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Импорт отменён',
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось отменить импорт'),
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** POST /api/exchange/imports/{type}/cancel/ с uuid (выгрузка файла черновика из памяти) */
    async cancelImportByUuid(importType: string, uuid: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(`${IMPORTS_URL}/${importType}/cancel/`, { uuid })
        await this.fetchUnfinishedImports(importType)
        feedbackStore.showToast({
          type: 'success',
          message: 'Черновик импорта удалён',
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось отменить черновик'),
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Восстановление черновика импорта по UUID */
    async resumeImportDraft(uuid: string, importType?: string): Promise<{ step: 'mapping' | 'preview' } | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const type = importType || this.activeImportType
        const resumeUrl = type ? `${IMPORTS_URL}/${type}/${uuid}/resume/` : `${IMPORTS_URL}/${uuid}/resume/`
        const resumeRes = await axios.get(resumeUrl)
        const data = resumeRes.data
        this.upload = {
          uuid: data.uuid,
          file_name: data.file_name,
          file_columns: data.file_columns || [],
          first_row: data.first_row || {},
          required_columns: data.required_columns || [],
          recognized_columns: data.recognized_columns || [],
          mapping: data.mapping || {},
          unrecognized_columns: data.unrecognized_columns || [],
          unmapped_file_columns: data.unmapped_file_columns || [],
        }

        const targetStep = data.step === 'preview' ? 'preview' : 'mapping'
        if (targetStep === 'preview') {
          const rowsRes = await this.fetchImportRows(uuid, 'all', 1, 25, type || undefined)
          if (rowsRes) {
            this.validation = {
              is_valid: rowsRes.error_rows_count === 0,
              total_rows: rowsRes.total_rows,
              valid_rows_count: rowsRes.valid_rows_count,
              error_rows_count: rowsRes.error_rows_count,
              warning_rows_count: rowsRes.warning_rows_count,
              errors: [],
              warnings: [],
              columns_summary: data.columns_summary || {},
              preview_rows: [],
              uuid: data.uuid,
              file_name: data.file_name,
            }
          }
        }
        return { step: targetStep }
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось восстановить черновик импорта'),
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Centrifuge import_completed. Тост и обновление статусов. */
    applyImportCompleted(payload: any) {
      const rawData = payload?.data ?? payload ?? {}
      const feedbackStore = useFeedbackStore()

      let completedItem: any = null
      let tasksList: IImportActiveItem[] | null = null

      if (Array.isArray(rawData)) {
        tasksList = rawData as IImportActiveItem[]
        completedItem = tasksList.find(t => t.status !== 'in_progress' && t.status !== 'pending') || tasksList[0]
      } else {
        completedItem = rawData
      }

      const name = completedItem?.file_name?.trim() || 'файл'
      const status = completedItem?.status

      if (status === 'failed') {
        feedbackStore.showToast({
          type: 'error',
          message: `Импорт «${name}» завершился с ошибкой`,
          time: 5000,
        })
      } else if (status === 'completed_with_errors') {
        feedbackStore.showToast({
          type: 'warning',
          message: `Импорт «${name}» завершён с ошибками`,
          time: 5000,
        })
      } else if (status === 'cancelled') {
        feedbackStore.showToast({
          type: 'success',
          message: `Импорт «${name}» отменён`,
          time: 4000,
        })
      } else {
        feedbackStore.showToast({
          type: 'success',
          message: `Импорт «${name}» завершён`,
          time: 5000,
        })
      }

      // Если пришёл обновлённый список задач, обновляем activeTasks прямо из сокета без HTTP GET
      if (tasksList) {
        const currentType = this.activeImportType?.toLowerCase()
        const matchingTasks = currentType
          ? tasksList.filter(t => isMatchingImportType(t.import_type, currentType))
          : tasksList

        const activeList = matchingTasks.filter(t => t.status === 'in_progress' || t.status === 'pending')
        this.activeTasks = activeList
        this.activeImport = activeList.find(t => t.status === 'in_progress') || activeList[0] || null

        // Синхронизируем историю
        if (this.history.length > 0) {
          for (const task of tasksList) {
            const histIndex = this.history.findIndex(h => Number(h.id) === Number(task.id))
            if (histIndex !== -1) {
              const hist = this.history[histIndex]
              this.history[histIndex] = {
                ...hist,
                percent: task.percent ?? 100,
                status: task.status || hist.status,
                status_display: task.status_display || hist.status_display,
                can_cancel: false,
                can_resume: false,
              }
            }
          }
        }
      } else {
        const id = completedItem?.import_history_id != null ? Number(completedItem.import_history_id) : (completedItem?.id != null ? Number(completedItem.id) : null)
        if (id != null) {
          this.activeTasks = this.activeTasks.filter(t => Number(t.id) !== id)
          this.activeImport = this.activeTasks.find(t => t.status === 'in_progress') || this.activeTasks[0] || null
          const hist = this.history.find(h => Number(h.id) === id)
          if (hist) {
            hist.status = completedItem.status || 'completed'
            hist.status_display = completedItem.status_display || 'Завершено'
            hist.can_cancel = false
            hist.can_resume = false
            hist.percent = completedItem.percent ?? 100
          }
        }
      }

      // Лишний HTTP fetchActiveImport больше не делаем, сокет уже передал актуальные задачи.
      if (this.activeImportType) {
        void this.fetchUnfinishedImports(this.activeImportType)
      }
    },


    async executeImport(
      importType: string,
      uuid: string,
      columnMapping: Record<string, string>,
      skipInvalidRows: boolean,
      corrections: IImportCellCorrection[] = [],
      extraParams: Record<string, string> = {},
    ): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IImportHistory>(`${IMPORTS_URL}/${importType}/execute/`, {
          uuid,
          column_mapping: columnMapping,
          skip_invalid_rows: skipInvalidRows,
          corrections: corrections.map(item => ({
            row: Number(item.row),
            column: String(item.column),
            value: item.value == null ? '' : String(item.value),
          })),
          extra_params: extraParams,
          async_mode: true,
        })
        const queued = response.status === 202 || response.data?.status === 'in_progress' || response.data?.status === 'pending'
        feedbackStore.showToast({
          type: 'success',
          message: queued ? 'Импорт поставлен в очередь' : 'Импорт завершён',
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: importErrorMessage(error, 'Не удалось выполнить импорт'),
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})
